import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { useAree, useCatalogo, useSessione } from '../servizi/sessione.tsx'
import { Bottone, Campo, Etichetta, EtichettaArea, Riquadro, Scelta, Tabella } from '../componenti/base.tsx'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { SceltaComune, type ComuneScelto } from './Ente.tsx'

type Cella = { persone: number | null; interventi: number | null; ore: string | null; soppressa: boolean }
type Riepilogo = {
  soglia: { normale: number; sensibile: number }
  totali: { persone: number; interventi: number; ore: string; ore_mediazione?: number }
  per_area: (Cella & { area_codice: string; area: string; colore: string | null })[]
  per_mese: (Cella & { mese: string })[]
  per_fascia: (Cella & { fascia: string })[]
  per_genere: (Cella & { genere: string })[]
  /** Comune dell'intervento (D-20); nome «non rilevato» per gli interventi senza comune. */
  per_comune: (Cella & { comune: string | null; nome: string; provincia_sigla: string | null })[]
}

/** Una voce di un campo del modulo della tipologia, con le etichette della versione (D-26). */
type VoceModulo = {
  campo: string; etichetta_campo: string; voce: string; etichetta_voce: string
  dato_sensibile: boolean; soppressa: boolean
}
type RisposteModuli = {
  soglia: { normale: number; sensibile: number }
  tipologie: { codice: string; nome: string; area_codice: string }[]
  tipologia: { codice: string; nome: string } | null
  interventi: (VoceModulo & { persone: number | null; interventi: number | null })[]
  attivita_collettive: (VoceModulo & { attivita: number | null; partecipanti: number | null })[]
}

/**
 * Report aggregati. Le celle che contano meno beneficiari della soglia non sono mostrate:
 * un valore basso renderebbe identificabili i beneficiari conteggiati. La soglia e' definita
 * dall'amministratore: di norma 5, e 10 sui temi sensibili.
 */
export function Report() {
  const { puo } = useSessione()
  const aree = useAree()
  // I codici del genere (M, F, NON_BINARIO, NON_DICHIARATO) si mostrano con l'etichetta del catalogo.
  const generi = useCatalogo('genere')
  const [dal, setDal] = useState(`${new Date().getFullYear()}-01-01`)
  const [al, setAl] = useState(new Date().toISOString().slice(0, 10))
  const [area, setArea] = useState('')
  // Nei report il comune e' quello dell'intervento (D-20), non la residenza della persona.
  const [comune, setComune] = useState<ComuneScelto | null>(null)
  const [dati, setDati] = useState<Riepilogo | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esportando, setEsportando] = useState(false)
  const [messaggio, setMessaggio] = useState<string | null>(null)

  useEffect(() => {
    setDati(null)
    const q = new URLSearchParams({ dal, al })
    if (area) q.set('area', area)
    if (comune) q.set('comune', comune.codice_istat)
    api.get<Riepilogo>(`/report/riepilogo?${q}`)
      .then(setDati)
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [dal, al, area, comune])

  // Campi dei moduli (D-26). Senza tipologia la risposta porta solo le tipologie che hanno un
  // modulo, per la tendina; scelta la tipologia arrivano i conteggi per voce.
  const [tipologiaModulo, setTipologiaModulo] = useState('')
  const [moduli, setModuli] = useState<RisposteModuli | null>(null)
  useEffect(() => {
    const q = new URLSearchParams({ dal, al })
    if (area) q.set('area', area)
    if (comune) q.set('comune', comune.codice_istat)
    if (tipologiaModulo) q.set('tipologia', tipologiaModulo)
    // Con filtri cambiati in fretta la risposta di una richiesta superata non deve sostituire
    // quella dell'ultima.
    let vivo = true
    api.get<RisposteModuli>(`/report/moduli?${q}`)
      .then((r) => { if (vivo) setModuli(r) })
      .catch((e) => { if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') })
    return () => { vivo = false }
  }, [dal, al, area, comune, tipologiaModulo])

  async function esporta() {
    setEsportando(true); setMessaggio(null)
    try {
      await api.post('/report/esporta', {
        tipo: 'aggregato', formato: 'xlsx', parametri: { dal, al, area, ...(comune ? { comune: comune.codice_istat } : {}) },
      })
      setMessaggio('in_preparazione')
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setEsportando(false) }
  }

  const massimo = Math.max(1, ...(dati?.per_mese ?? []).map((m) => m.interventi ?? 0))

  return (
    <>
      <Intestazione titolo="Report"
        sottotitolo="Dati aggregati. Le celle sotto soglia non sono mostrate."
        azioni={puo('export.aggregato')
          ? <>
              <Link className="bottone bottone-discreto" to="/esportazioni">Le mie esportazioni</Link>
              <Bottone variante="secondario" onClick={() => void esporta()} inCorso={esportando}>
                Esporta in XLSX
              </Bottone>
            </>
          : undefined} />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && (
        <Avviso tipo="successo">
          Esportazione in preparazione: quando è pronta arriva una notifica, e il file si scarica da{' '}
          <Link to="/esportazioni">Le mie esportazioni</Link>.
        </Avviso>
      )}

      <Riquadro>
        <div className="filtri-report">
          <Campo etichetta="Dal" tipo="date" valore={dal} cambia={setDal} />
          <Campo etichetta="Al" tipo="date" valore={al} cambia={setAl} />
          <Scelta etichetta="Area" valore={area}
                  cambia={(v) => {
                    setArea(v)
                    // La tipologia dei campi del modulo resta scelta solo se appartiene all'area.
                    setTipologiaModulo((t) => (
                      !v || moduli?.tipologie.find((x) => x.codice === t)?.area_codice === v ? t : ''))
                  }}
                  vuota="Tutte le aree"
                  opzioni={aree.aree.map((a) => ({ valore: a.codice, etichetta: a.nome }))} />
          <SceltaComune etichetta="Comune dell’intervento" vuota="Tutti i comuni" valore={comune} cambia={setComune} />
        </div>
      </Riquadro>

      {!dati ? <Attesa testo="Caricamento…" /> : (
        <>
          <div className="griglia griglia-riquadri">
            <Numero titolo="Beneficiari" valore={dati.totali.persone} />
            <Numero titolo="Interventi" valore={dati.totali.interventi} />
            <Numero titolo="Ore" valore={dati.totali.ore ?? '0'} />
            <Numero titolo="Ore di mediazione" valore={dati.totali.ore_mediazione ?? 0} />
            <Numero titolo="Soglia di riservatezza" valore={`≥ ${dati.soglia.normale}`}
                    nota={`${dati.soglia.sensibile} sui temi sensibili`} />
          </div>

          <Riquadro titolo="Per area" denso>
            <Tabella intestazioni={['Area', 'Beneficiari', 'Interventi', 'Ore']} vuoto="Nessun dato.">
              {dati.per_area.map((r) => (
                <tr key={r.area_codice}>
                  <td data-etichetta="Area">
                    <EtichettaArea codice={r.area_codice} colore={r.colore ?? aree.coloreDi(r.area_codice)} />
                    {' '}{r.area}
                  </td>
                  <CelleNumeriche cella={r} />
                </tr>
              ))}
            </Tabella>
          </Riquadro>

          <Riquadro titolo="Andamento mensile">
            {dati.per_mese.length === 0 ? <p className="tenue">Nessun dato.</p> : (
              <div className="istogramma">
                {dati.per_mese.map((m) => (
                  <div key={m.mese} className="barra-gruppo"
                       title={`${m.mese}: ${m.interventi ?? 'sotto soglia'}`}>
                    {/* L'altezza e' l'unico valore calcolato dal dato: resta in linea. */}
                    <div className="barra" style={{ height: `${((m.interventi ?? 0) / massimo) * 100}%` }} />
                    <span className="piccolo tenue">{m.mese.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </Riquadro>

          <div className="griglia griglia-riquadri">
            <Riquadro titolo="Per fascia d’età">
              <Barrette righe={dati.per_fascia.map((f) => ({ nome: f.fascia, ...f }))} />
            </Riquadro>
            <Riquadro titolo="Per genere">
              <Barrette righe={dati.per_genere.map((g) => ({ nome: generi.nomeDi(g.genere), ...g }))} />
            </Riquadro>
          </div>

          <Riquadro titolo="Per comune dell’intervento" denso>
            <Tabella intestazioni={['Comune', 'Beneficiari', 'Interventi', 'Ore']} vuoto="Nessun dato.">
              {(dati.per_comune ?? []).map((r) => (
                <tr key={r.comune ?? 'non rilevato'}>
                  <td data-etichetta="Comune">
                    {r.comune ? `${r.nome}${r.provincia_sigla ? ` (${r.provincia_sigla})` : ''}` : <span className="tenue">{r.nome}</span>}
                  </td>
                  <CelleNumeriche cella={r} />
                </tr>
              ))}
            </Tabella>
          </Riquadro>
        </>
      )}

      {moduli && moduli.tipologie.length > 0 && (
        <Riquadro titolo="Campi della tipologia">
          <div className="filtri-report">
            <Scelta etichetta="Tipologia" valore={tipologiaModulo} cambia={setTipologiaModulo}
                    vuota="— seleziona —"
                    opzioni={moduli.tipologie.filter((t) => !area || t.area_codice === area)
                      .map((t) => ({ valore: t.codice, etichetta: t.nome }))}
                    aiuto="Voci dei campi a scelta del modulo, nel periodo e nell’area indicati." />
          </div>
          {tipologiaModulo && moduli.tipologia?.codice === tipologiaModulo
            && moduli.interventi.length === 0 && moduli.attivita_collettive.length === 0 && (
            <p className="tenue">Nessun dato.</p>
          )}
          {[...moduli.interventi, ...moduli.attivita_collettive].some((r) => r.dato_sensibile) && (
            <p className="piccolo tenue">
              Sui campi con dati particolari la soglia è {moduli.soglia.sensibile}.
            </p>
          )}
        </Riquadro>
      )}

      {moduli && moduli.tipologia?.codice === tipologiaModulo && moduli.interventi.length > 0 && (
        <Riquadro titolo="Interventi per voce" denso>
          <Tabella intestazioni={['Campo', 'Voce', 'Beneficiari', 'Interventi']}>
            {moduli.interventi.map((r) => (
              <tr key={`${r.campo} ${r.voce}`}>
                <td data-etichetta="Campo">{r.etichetta_campo}</td>
                <td data-etichetta="Voce">{r.etichetta_voce}</td>
                {r.soppressa
                  ? (
                    <td data-etichetta="Beneficiari" colSpan={2}>
                      <Etichetta tono="attenzione">sotto soglia: non mostrabile</Etichetta>
                    </td>
                  )
                  : (
                    <>
                      <td data-etichetta="Beneficiari" className="mono">{r.persone}</td>
                      <td data-etichetta="Interventi" className="mono">{r.interventi}</td>
                    </>
                  )}
              </tr>
            ))}
          </Tabella>
        </Riquadro>
      )}

      {moduli && moduli.tipologia?.codice === tipologiaModulo && moduli.attivita_collettive.length > 0 && (
        <Riquadro titolo="Attività collettive per voce" denso>
          <Tabella intestazioni={['Campo', 'Voce', 'Attività', 'Partecipanti']}>
            {moduli.attivita_collettive.map((r) => (
              <tr key={`${r.campo} ${r.voce}`}>
                <td data-etichetta="Campo">{r.etichetta_campo}</td>
                <td data-etichetta="Voce">{r.etichetta_voce}</td>
                {r.soppressa
                  ? (
                    <td data-etichetta="Attività" colSpan={2}>
                      <Etichetta tono="attenzione">sotto soglia: non mostrabile</Etichetta>
                    </td>
                  )
                  : (
                    <>
                      <td data-etichetta="Attività" className="mono">{r.attivita}</td>
                      <td data-etichetta="Partecipanti" className="mono">{r.partecipanti}</td>
                    </>
                  )}
              </tr>
            ))}
          </Tabella>
        </Riquadro>
      )}
    </>
  )
}

function Numero({ titolo, valore, nota }: { titolo: string; valore: string | number; nota?: string }) {
  return (
    <Riquadro>
      <div className="piccolo tenue">{titolo}</div>
      <div className="numero-report">{valore}</div>
      {nota && <div className="piccolo tenue">{nota}</div>}
    </Riquadro>
  )
}

function CelleNumeriche({ cella }: { cella: Cella }) {
  if (cella.soppressa) {
    return (
      <>
        <td data-etichetta="Beneficiari" colSpan={3}>
          <Etichetta tono="attenzione">sotto soglia: non mostrabile</Etichetta>
        </td>
      </>
    )
  }
  return (
    <>
      <td data-etichetta="Beneficiari" className="mono">{cella.persone}</td>
      <td data-etichetta="Interventi" className="mono">{cella.interventi}</td>
      <td data-etichetta="Ore" className="mono">{cella.ore ?? '—'}</td>
    </>
  )
}

function Barrette({ righe }: { righe: (Cella & { nome: string })[] }) {
  const massimo = Math.max(1, ...righe.map((r) => r.persone ?? 0))
  if (!righe.length) return <p className="tenue">Nessun dato.</p>
  return (
    <div className="colonna">
      {righe.map((r) => (
        <div key={r.nome}>
          <div className="barretta-capo piccolo">
            <span>{r.nome}</span>
            <span className="mono tenue">{r.soppressa ? 'sotto soglia' : r.persone}</span>
          </div>
          <div className="barretta">
            {/* La larghezza e' l'unico valore calcolato dal dato: resta in linea. */}
            <div className={`barretta-valore${r.soppressa ? ' sotto-soglia' : ''}`}
                 style={{ width: r.soppressa ? '4%' : `${((r.persone ?? 0) / massimo) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}
